// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { getSessionUser } from '@/lib/authSession';

const ClaimAccount: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    verifyToken();
  }, []);

  const verifyToken = async () => {
    try {
      // Check for token in URL params (supports both ?token= and ?access_token=)
      const token = searchParams.get('token') || searchParams.get('access_token');
      
      if (!token) {
        setTokenValid(false);
        setLoading(false);
        return;
      }

      // Verify the session by getting current user
      const { data: { user }, error } = await getSessionUser();
      
      if (error || !user) {
        setTokenValid(false);
      } else {
        setTokenValid(true);
      }
    } catch (error) {
      console.error('Error verifying token:', error);
      setTokenValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimAccount = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (password.length < 8) {
      toast({
        title: "Password too short",
        description: "Password must be at least 8 characters",
        variant: "destructive",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure both passwords are the same",
        variant: "destructive",
      });
      return;
    }

    setClaiming(true);

    try {
      // Update user password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password,
      });

      if (updateError) throw updateError;

      // Get current user
      const { data: { user }, error: userError } = await getSessionUser();
      
      console.log('🔍 DEBUG - Got user after password set:', {
        userId: user?.id,
        email: user?.email,
        userError: userError
      });
      
      if (userError || !user) {
        console.error('❌ Could not get user:', userError);
        throw new Error('Could not get user');
      }

      if (user?.email) {
        console.log('📧 User email confirmed:', user.email);
        
        // Update athlete_import_records to mark as claimed
        console.log('⏳ Attempting to update athlete_import_records...');
        const { data: recordData, error: recordError } = await supabase
          .from('athlete_import_records')
          .update({ password_set_at: new Date().toISOString() })
          .eq('athlete_email', user.email)
          .select(); // IMPORTANT: Add .select() to see what was updated
        
        if (recordError) {
          console.error('❌ Error updating import record:', recordError);
        } else {
          console.log('✅ Updated athlete_import_records:', recordData);
          console.log('📊 Number of rows updated:', recordData?.length || 0);
        }

        // Also update profiles.claimed_at
        console.log('⏳ Attempting to update profiles...');
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .update({ claimed_at: new Date().toISOString() })
          .eq('email', user.email)
          .select(); // IMPORTANT: Add .select() to see what was updated
        
        if (profileError) {
          console.error('❌ Error updating profile:', profileError);
        } else {
          console.log('✅ Updated profiles:', profileData);
          console.log('📊 Number of rows updated:', profileData?.length || 0);
        }
      } else {
        console.error('❌ User email is null or undefined');
      }

      setSuccess(true);
      
      toast({
        title: "Success!",
        description: "Your account has been claimed successfully",
      });

      // Redirect after 2 seconds
      setTimeout(() => {
        navigate('/athlete-profile');
      }, 2000);

    } catch (error: any) {
      console.error('Error claiming account:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to claim account",
        variant: "destructive",
      });
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-700 via-primary-600 to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary-700 mb-4" />
            <p className="text-muted-foreground">Verifying your link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-700 via-primary-600 to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <AlertCircle className="h-16 w-16 text-destructive" />
            </div>
            <CardTitle className="text-center">Invalid Link</CardTitle>
            <CardDescription className="text-center">
              This link is invalid or has expired
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => navigate('/')} 
              className="w-full"
            >
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-700 via-primary-600 to-secondary flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex justify-center mb-4">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
            <CardTitle className="text-center">Account Claimed!</CardTitle>
            <CardDescription className="text-center">
              Redirecting you to your profile...
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary-700" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-700 via-primary-600 to-secondary flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Claim Your Account</CardTitle>
          <CardDescription>
            Set up your password to complete your account setup
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleClaimAccount} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  required
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
            </div>

            <p className="text-sm text-muted-foreground">
              Password must be at least 8 characters
            </p>

            <Button
              type="submit"
              className="w-full"
              disabled={claiming}
            >
              {claiming ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Claiming Account...
                </>
              ) : (
                'Claim My Account'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClaimAccount;
